import { useState, useCallback, useEffect, useRef } from "react";
import {
  ValidationError,
  validateDate,
  buildDateString,
} from "./date-validation.helpers";
import { useValueSync } from "./use-value-sync.hook";

type DateInputProps = {
  value?: string;
  required?: boolean;
  minDate?: string;
  maxDate?: string;
  onChange?: (value: string) => void;
  onValidityChange?: (validationErrors: ValidationError[]) => void;
};

/**
 * Hook for managing date input fields with validation and value syncing
 */
export const useDateInput = ({
  value = "",
  required,
  minDate,
  maxDate,
  onChange,
  onValidityChange,
}: DateInputProps) => {
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");
  const [nativeInputValue, setNativeInputValue] = useState("");
  const lastEmittedValue = useRef("");

  const onValidityChangeRef = useRef(onValidityChange);
  onValidityChangeRef.current = onValidityChange;

  const validate = useCallback(() => {
    const errors = validateDate(month, day, year, required, minDate, maxDate);
    onValidityChangeRef.current?.(errors);
  }, [month, day, year, required, minDate, maxDate]);

  useEffect(() => validate(), [validate]);

  useValueSync({
    value,
    setMonth,
    setDay,
    setYear,
    setNativeInputValue,
    lastEmittedValue,
  });

  /**
   * Emits a complete date if all fields are valid, emits an empty string if all fields are empty
   */
  const emitDate = useCallback(
    (newMonth: string, newDay: string, newYear: string) => {
      const errors = validateDate(
        newMonth,
        newDay,
        newYear,
        required,
        minDate,
        maxDate,
      );
      onValidityChangeRef.current?.(errors);

      // If any field is missing, emit empty string
      if (!newMonth || !newDay || !newYear) {
        onChange?.("");
        lastEmittedValue.current = "";
        setNativeInputValue("");
        return true;
      }

      const newDateString = buildDateString(newMonth, newDay, newYear);
      if (!errors.some((error) => error === "invalid-date") && newDateString) {
        onChange?.(newDateString);
        lastEmittedValue.current = newDateString;
        setNativeInputValue(newDateString);
        return true;
      }

      return false;
    },
    [onChange, required, minDate, maxDate],
  );

  return {
    month,
    day,
    year,
    nativeInputValue,
    setMonth,
    setDay,
    setYear,
    emitDate,
  };
};
